import { BaseController } from "../../common/base/controller";
import { FeedbackModel } from "../../common/ui/feedback/feedback.model";

import { finishStateName } from "../finish/finish.state";
import { statusStateName } from "../status/status.state";

import _ from "lodash";

export class OverviewController extends BaseController {
	columnsClass;
	columnClass;
	columnClassOwner;

	feedback = new FeedbackModel();

	constructor(
		$q,
		$scope,
		$uibModal,
		configService,
		controllerService,
		feedbackService,
		orderService,
		overviewService,
		uiModalDeclarationService,
		errorTrackingService,
	) {
		"ngInject";
		super({
			$q,
			$scope,
			$uibModal,
			configService,
			controllerService,
			feedbackService,
			orderService,
			overviewService,
			uiModalDeclarationService,
			errorTrackingService,
		});

		this.dateOptions = { minDate: new Date() };
		this.setLanguageOptions().then(() => {
			this.dateOptions.dateFormat = this.languageOptions.mediumDate;
		});
	}

	get showDeadline() {
		return this.isPSD;
	}

	get showSignaturePad() {
		return this.configService.isFeatureActive(this.Features.showSignaturePad);
	}

	get hasStatusPage() {
		return this.configService.isFeatureActive(this.Features.hasStatusPage);
	}

	get hasFeedbackModal() {
		return this.configService.isFeatureActive(this.Features.hasFeedbackModal);
	}

	get skipRevocationRight() {
		return this.configService.isFeatureActive(
			this.Features.skipRevocationRight,
		);
	}

	get isOwnAccount() {
		return this.transfer.type === "ownAccount";
	}

	get ownerData() {
		// use oldOwners if there is data into - quick check
		return this.oldOwners.length >= 1 && this.oldOwners[0].firstName
			? this.oldOwners
			: this.newOwners;
	}

	get hasMultipleOwners() {
		return this.ownerData.length > 1;
	}

	get oldAccountOwnerTitle() {
		return this.isOwnAccount
			? "app.overviewState.securityAccountOwner"
			: "app.overviewState.securityAccountOldOwner" +
					(this.oldOwners.length > 1 ? "s" : "");
	}

	get newAccountOwnerTitle() {
		return this.isOwnAccount
			? "app.overviewState.securityAccountOwner"
			: "app.overviewState.securityAccountNewOwner" +
					(this.newOwners.length > 1 ? "s" : "");
	}

	get transferAllSecurities() {
		const oldPortfolio = this.appCache.get("oldPortfolio");
		return oldPortfolio && oldPortfolio.transferAllSecurities;
	}

	get isPSD() {
		return this.configService.isPSD();
	}

	get tenant() {
		return this.envService.getTenant();
	}

	get isAustrianTenant() {
		return this.configService.isAustrianTenant();
	}

	get bankName() {
		return this.configService.getName();
	}

	get showNoWertpapierleiheChechbox() {
		return this.configService.isConsorsbank();
	}

	get theme() {
		const { logo, primaryColor } = this.configService.config;
		return encodeURIComponent(
			angular.toJson({
				primaryColor,
				logo,
			}),
		);
	}

	$onInit() {
		// eslint-disable-next-line angular/window-service
		window.scrollTo(0, 0);

		// dont show mobile sign option when already be on a mobile device
		this.mobileSignUrl = this.isMobileDevice
			? ""
			: this.overviewService.getSignUrl();
		this.signatureServiceUrl = this.overviewService.getSignatureUrl();
		this.appName = "DW Service";

		this.oldPortfolio = this.appCache.get("oldPortfolio");
		this.newPortfolio = this.appCache.get("newPortfolio");
		this.oldOwners = this.appCache.get("oldOwners");
		this.newOwners = this.appCache.get("newOwners");
		this.oldBank = this.appCache.get("oldBank");
		this.newBank = this.appCache.get("newBank");
		this.transfer = this.appCache.get("transfer");
		this.model = {
			deadline: this.appCache.get("deadline"),
		};

		this.noWertpapierleiheErrorMessage = {
			required: "app.overviewState.noWertpapierleiheCheckbox.required",
		};

		this.appCache.remove("abdicationCancellationRight");

		this.setupLayout();

		if (this.showSignaturePad) {
			this.setSignatureValidators();
		}

		this.createTransferList();
		this.buildPreviewDocument();
	}

	buildPreviewDocument() {
		const order = this.orderData();

		this.previewDocument = this.overviewService
			.buildPreviewDocument(order)
			.then((resp) => {
				this.previewBlob = resp.data;
			});
	}

	createTransferList() {
		const informFinanzamtAboutSwitchOption = this.isAustrianTenant
			? {
					informFinanzamtAboutSwitch: this.transfer.informFinanzamtAboutSwitch,
				}
			: {};

		this.transferList = {
			...this.oldPortfolio,
			...informFinanzamtAboutSwitchOption,
		};
	}

	setupLayout() {
		this.columnsClass = { "is-multiline": !this.isOwnAccount };
		this.columnClass = { "is-6": !this.isOwnAccount };
		this.columnClassOwner = {
			"is-6": !this.isOwnAccount,
			"is-12": this.isOwnAccount,
		};
	}

	prev() {
		this.prevState();
	}

	submit() {
		this.form.$setSubmitted();

		if (this.form.$invalid) {
			return;
		}

		if (this.oldPortfolio.data) {
			delete this.oldPortfolio.data;
		}

		if (this.showSignaturePad) {
			// Check for missing signature
			let hasMissingSignatures = false;
			for (let customer in this.ownerData) {
				if (this.signatureIsMissing(this.ownerData[customer])) {
					this.missingSignatures[customer] = hasMissingSignatures = true;
				}
			}
			if (hasMissingSignatures) {
				return;
			}
		}

		this.appCache.set(
			"abdicationCancellationRight",
			this.model.abdicationCancellationRight || true,
		);

		// Check signature
		if (!this.showSignaturePad) {
			this.trySendOrder();
			return;
		}

		// Check abdication cancellation right (reject right of withdrawal)
		const { skipRevocationRight } = this;
		if (skipRevocationRight || this.model.abdicationCancellationRight) {
			this.trySendOrder();
			return;
		}

		// Confirm for missing abdication cancellation right
		if (!this.model.abdicationCancellationRight) {
			// Confirm for missing abdication cancellation right
			this.confirmMissingAbdicationCancellationRight().then(() => {
				this.trySendOrder();
			});
			return;
		}
	}

	trySendOrder() {
		const order = this.orderData();
		this.postOrder(order);
	}

	orderData() {
		let data = {
			oldBank: this.oldBank,
			newBank: this.newBank,
			oldPortfolio: this.oldPortfolio,
			newPortfolio: this.newPortfolio,
			oldOwners: this.oldOwners,
			newOwners: this.newOwners,
			transfer: this.transfer,
			overview: this.model,
		};

		return this.orderService.createOrder(data);
	}

	postOrder(order) {
		this.orderError = null;

		// Track event for posting order
		this.trackingService.trackEvent("order-init", {
			category: "overview",
			label: "request",
			tenant: this.tenant,
		});

		return this.overviewService
			.postOrder(order)
			.busy(this, "isBusy")
			.then(
				(data) => this.postOrderDone(data),
				(error) => this.postOrderError(error),
			);
	}

	postOrderDone(data) {
		// Store config to reset before clearing cache
		const config = this.appCache.get("config");
		const abdicationCancellationRight = this.appCache.get(
			"abdicationCancellationRight",
		);

		const {
			orderId,
			documentId,
			documentToken,
			investmentApplicationRedirect,
			portfolioApplicationRedirect,
			jwt,
		} = data;

		// get the birthday of the user where the status link is send to via email
		const maybeUserWithStatusEmail = _.find(
			[...this.newOwners, ...this.oldOwners],
			(o) => o.email,
		);

		// when userEmail feature is disabled there is no user
		let birthDate = null;
		if (maybeUserWithStatusEmail) {
			birthDate = maybeUserWithStatusEmail.birthDate;
		}

		// Track event for successfully posted order
		this.trackingService.trackEvent("order-success", {
			category: "overview",
			label: "request",
			tenant: this.tenant,
		});

		this.prepareCache({
			config,
			orderId,
			documentId,
			documentToken,
			birthDate,
			jwt,
			abdicationCancellationRight,
			investmentApplicationRedirect,
			portfolioApplicationRedirect,
		});

		if (this.hasStatusPage) {
			if (this.hasFeedbackModal) {
				return this.askForFeedback().finally(() => {
					// When feedback has not been submitted, track event for skipped feedback
					const feedback = this.appCache.get("feedback");
					if (feedback) {
						this.feedbackService.trackFeedback(feedback, true);
					}
					this.go(statusStateName, { orderId });
				});
			}
			return this.go(statusStateName, { orderId });
		}
		return this.go(finishStateName);
	}

	postOrderError(error) {
		this.$log.error(error);

		if (!(this.oldBank.legacyBankIdentifier || this.oldBank.bankCode)) {
			this.bankCodeNotPresent(this.oldBank);
		}

		const { data, status, statusText } = error;
		this.orderError = { data, status, statusText };

		// Track event for failed posted order
		this.trackingService.trackEvent("order-error", {
			category: "overview",
			label: "request",
			value: error.message,
			tenant: this.tenant,
		});
	}

	bankCodeNotPresent(oldBank) {
		this.trackingService.trackEvent("order-error-bank-code-not-present", {
			category: "overview",
			label: "request",
			value: oldBank.shortName,
			tenant: this.tenant,
		});

		this.errorTrackingService.captureException(
			"Submitted Order without legacy bank identifier",
			{
				extra: {
					tenant: this.tenant,
					oldBank,
				},
			},
		);
	}

	prepareCache(cacheValues) {
		// Clear complete process cache before setting finish values
		this.appCache.clear();

		Object.keys(cacheValues).forEach((key) => {
			if (cacheValues[key]) {
				this.appCache.set(key, cacheValues[key]);
			}
		});
	}

	askForFeedback() {
		this.appCache.set("feedback", null);

		const defer = this.$q.defer();

		this.uiModalDeclarationService.openUrl({
			url: "app/common/ui/modals/feedback.tpl.html",
			options: {
				scope: this.$scope,
				modalClass: "feedback-modal",
				onSuccess: () => defer.resolve(),
				onDissmiss: () => defer.reject(),
			},
		});

		return defer.promise;
	}

	confirmMissingAbdicationCancellationRight() {
		const { $q, $scope } = this;

		$scope.confirmTitle =
			"app.overviewState.abdicationCancellationRight.confirm.title";
		$scope.confirmBody =
			"app.overviewState.abdicationCancellationRight.confirm.body";
		$scope.confirmDismiss =
			"app.overviewState.abdicationCancellationRight.confirm.dismiss";
		$scope.confirmOk =
			"app.overviewState.abdicationCancellationRight.confirm.ok";
		$scope.showAbdicationCancellationRight = true;

		const defer = $q.defer();
		this.uiModalDeclarationService.openUrl({
			url: "app/common/ui/modals/confirmation.tpl.html",
			options: {
				scope: $scope,
				onSuccess: () => defer.resolve(),
				onDissmiss: () => defer.reject(),
				modalClass: "confirmation-modal",
			},
		});
		return defer.promise;
	}

	signatureIsMissing(customer) {
		return (
			!customer.signature ||
			customer.signature.dataUrl ===
				"data:image/gif;base64,R0lGODlhAQABAAAAACwAAAAAAQABAAA="
		);
	}

	setSignatureValidators() {
		// manage visibility of missing signature messages
		this.missingSignatures = {};
		for (let user in this.ownerData) {
			this.ownerData[user].signature = null;
			this.missingSignatures[user] = false;

			// this is to toggle missing signature message, if signature was made.
			this.$scope.$watch(
				"vm." +
					(this.oldOwners.length >= 1 && this.oldOwners[0].firstName
						? "oldOwners"
						: "newOwners") +
					"[" +
					user +
					"].signature",
				(newVal, oldVal) => {
					if (oldVal) {
						this.missingSignatures[user] = this.signatureIsMissing({
							signature: newVal,
						});
					}
				},
			);
		}
	}
}



// WEBPACK FOOTER //
// ./src/app/components/overview/overview.controller.js